<template>
  <input
    :key="chaveFormato"
    v-money="configuracao"
    class="v-money"
    type="tel"
    :value="valorFormatado"
    @change="aoMudar"
  />
</template>

<script>
// `v-money` stopped at Vue 2; `v-money3` continues it with the same directive.
// The swap fits in this one file because all 128 money fields go through <money-input>.
import { VMoney3 } from 'v-money3';

/**
 * Campo monetário da aplicação.
 *
 * Existe para concentrar num arquivo só a dependência de máscara de dinheiro. Hoje ele
 * usa a diretiva do `v-money`, exatamente a mesma que o componente `<money>` usava, e
 * por isso a troca dos 127 campos por `<money-input>` NÃO muda comportamento nenhum: o
 * motor de formatação é o mesmo, byte a byte.
 *
 * O ganho aparece na janela do salto. `v-money` é de 2018, não tem versão para Vue 3, e
 * suas diretivas usam os hooks antigos (`bind`/`inserted`/`update`), removidos na v3.
 * Com esta indireção, trocar o motor passa a ser mexer em UM arquivo em vez de 127 — e
 * 127 campos de dinheiro alterados de uma vez, num ERP financeiro sem cobertura de
 * teste, é o cenário em que um valor errado chega ao backend com HTTP 200.
 *
 * DUAS COISAS QUE NÃO PODEM MUDAR AQUI:
 *
 * 1. A classe `v-money` no input. Dez arquivos decidem o pula-campo do operador de caixa
 *    com `this.$refs[campo].$el.classList.contains('v-money')` — se a classe sumir, o
 *    Enter deixa de pular para o próximo campo durante a digitação rápida, e o sintoma é
 *    um TypeError que só aparece quando alguém aperta Enter.
 *
 * 2. O TIPO emitido. Com `masked: false` — 58 ocorrências no projeto — o evento `input`
 *    carrega um Number, não uma String. O backend aceita string com HTTP 200 e grava
 *    valor errado, então isto é fixado por spec.
 */
export default {
  name: 'MoneyInput',

  directives: { money: VMoney3 },

  // Declarado porque o Vue 3 exige (e o `require-explicit-emits` do placar cobra):
  // evento não declarado cai no fallthrough de atributos e vira listener nativo no
  // elemento raiz, disparando duas vezes. No Vue 2.7 a chave é simplesmente ignorada.
  //
  // São DOIS eventos de propósito. Ver o comentário de `modelValue` abaixo.
  emits: ['input', 'update:modelValue'],

  props: {
    /**
     * O contrato do Vue 2: `value` + evento `input`. É como os 123 pontos que passam
     * `:value`/`@input` explicitamente chamam este componente, e continua válido no
     * Vue 3 — ali `value` é só uma prop com nome comum, e `input` um evento custom.
     *
     * `required` saiu e o default virou `undefined` (não 0) porque as duas coisas
     * juntas eram contraditórias, e porque agora é preciso distinguir "não passou"
     * de "passou zero" para escolher entre os dois contratos.
     */
    value: { type: [Number, String], default: undefined },

    /**
     * O contrato do Vue 3: `modelValue` + `update:modelValue`. Cinco pontos usam
     * `v-model` neste componente (marketplace.vue:351,379,406,433 e
     * resumo_tfl.vue:241), e no Vue 3 `v-model` compila para ESTE par — não para
     * `value`/`input`. Sem esta prop, os cinco quebrariam em silêncio: campo de
     * dinheiro que renderiza vazio e não devolve o que foi digitado.
     *
     * Declarar os dois custa duas linhas e evita reescrever os 123 pontos do outro
     * contrato. No Vue 2 esta prop nunca é passada e o `update:modelValue` não tem
     * ouvinte — é inerte. É a mesma ideia que justificou o wrapper: a incompatibilidade
     * fica num arquivo, não em 128.
     */
    modelValue: { type: [Number, String], default: undefined },
    masked: { type: Boolean, default: false },
    precision: { type: Number, default: 2 },
    decimal: { type: String, default: ',' },
    thousands: { type: String, default: '.' },
    prefix: { type: String, default: '' },
    suffix: { type: String, default: '' },
  },

  computed: {
    configuracao() {
      const { precision, decimal, thousands, prefix, suffix } = this;
      return { precision, decimal, thousands, prefix, suffix };
    },

    // v-money3 ignores new options on a mounted directive (it only warns), while Vue 2's
    // v-money re-applied them on every update. A field that switches R$ <-> % kept the
    // old mask while aoMudar unformatted with the new one: 10 % went out as 10000 and a
    // R$ 10 mass adjustment as 1000000000. Keying the input on the format re-mounts the
    // directive with the new options, as Vue 2 behaved.
    chaveFormato() {
      return JSON.stringify(this.configuracao);
    },

    valorFormatado() {
      const valor = this.value !== undefined ? this.value : (this.modelValue ?? 0);
      // A Number has to be fixed to `precision` decimals BEFORE it reaches the input.
      // The money directive reads el.value as a string of digits and treats the last
      // `precision` of them as cents: 150 would be read as "150" -> R$ 1,50, and a
      // parent that feeds the emitted value back (q-field + v-model) would shrink it
      // again on every render — 150 -> 1.5 -> 0.015. v-money's own <money> component
      // did exactly this toFixed in its format(), which is why the 127 fields it
      // replaced never showed the problem. Strings pass through untouched, as they
      // did in <money>.
      return typeof valor === 'number' ? valor.toFixed(this.precision) : valor;
    },
  },

  watch: {
    // The format key re-creates the <input> (see chaveFormato). Screens that switch R$ <-> %
    // focus this field in the same handler (pulaCampo -> $el.focus()), before the re-render,
    // so the focus landed on the element about to be removed and the next keys were lost;
    // on Vue 2 the element was patched in place and kept it. Note whether it is focused
    // now, before this component re-renders, and give it back in updated().
    chaveFormato: {
      flush: 'pre',
      handler() {
        this.devolverFoco = typeof document !== 'undefined' && document.activeElement === this.$el;
      },
    },
  },

  updated() {
    if (!this.devolverFoco) return;
    this.devolverFoco = false;
    if (document.activeElement !== this.$el) this.$el.focus();
  },

  methods: {
    aoMudar(evento) {
      const bruto = evento.target.value;
      const valor = this.masked ? bruto : this.paraNumero(bruto);
      // Os dois contratos, sempre. Quem escuta um não vê o outro.
      // update:modelValue FIRST: a parent with both v-model and @input (marketplace.vue)
      // reads the model in its @input handler. Vue 2's compiler ran the v-model callback
      // before the other `input` listeners; emitting input first left the handler one
      // keystroke behind (the commission column lagged while typing).
      this.$emit('update:modelValue', valor);
      this.$emit('input', valor);
    },

    /**
     * Desformata na mesma ordem do v-money: tira prefixo/sufixo, remove o separador de
     * milhar, troca o decimal por ponto. Uma string vazia vira 0 — e não NaN, que o
     * backend gravaria como nulo.
     */
    paraNumero(texto) {
      const limpo = String(texto)
        .replace(this.prefix, '')
        .replace(this.suffix, '')
        .split(this.thousands)
        .join('')
        .replace(this.decimal, '.')
        .replace(/[^\d.-]/g, '');

      const numero = Number(limpo);
      return Number.isNaN(numero) ? 0 : numero;
    },
  },
};
</script>
